import { format } from 'date-fns'
import {
  Activity as ActivityIcon,
  ChartBar,
  CheckSquare,
  Clock,
  Flame,
  GalleryVerticalEnd,
  Loader2,
  Luggage,
  MapPin,
  NotebookPen,
  Route,
  Stamp,
  Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'

import { deleteVisit, getOverlandVisit, mergeVisits } from '@/api/services/overland'
import { DateNavigator } from '@/components/shared/date-navigator'
import { Header, type ToolbarItem } from '@/components/shared/header'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { ActionSection, RadioSection } from '@/components/ui/custom-dropdown'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { formatDateCompact } from '@/lib/format-utils'
import { PATHS } from '@/routes/paths'
import { useActivityReviewStore } from '@/store/useActivityReviewStore'
import { useAppStore } from '@/store/useAppStore'
import { useGarminStore } from '@/store/useGarminStore'
import { useOverlandStore } from '@/store/useOverlandStore'
import { useProgressStore } from '@/store/useProgressStore'

import { CalendarDayView } from './components/calendar-day-view'
import { GoalsView } from './components/goals-view'
import { HealthView } from './components/health'
import { InfiniteCalendarView } from './components/infinite-calendar-view'
import { InfiniteTimelineView } from './components/infinite-timeline-view'
import { JournalView } from './components/journal-view'
import { ReviewProgressBar } from './components/review-progress-bar'
import { StatsView } from './components/stats-view'
import { TimelineView } from './components/timeline-view'
import { useActivityRemoteSync } from './use-activity-remote-sync'
import { useVisitReceiptSync } from './use-visit-receipt-sync'

/** タイムラインの見た目。スクロール継続かどうかは別軸（SCROLL_PARAM）で持つ */
const VIEW_MODE = {
  DAILY: 'daily',
  CALENDAR: 'calendar',
} as const

type ViewMode = (typeof VIEW_MODE)[keyof typeof VIEW_MODE]

const VIEW_MODE_ITEMS: { value: ViewMode; label: string }[] = [
  { value: VIEW_MODE.DAILY, label: '日付ごと' },
  { value: VIEW_MODE.CALENDAR, label: 'カレンダー' },
]

const SCROLL_PARAM = 'scroll'

type TabValue = 'timeline' | 'stats' | 'goals' | 'journal' | 'health'

const TAB_VALUES: TabValue[] = ['timeline', 'stats', 'goals', 'journal', 'health']

// 基本タブ（全ユーザー）- カレンダータブを削除
const BASE_TABS = [
  { value: 'timeline' as const, label: 'タイムライン', icon: Clock },
  { value: 'stats' as const, label: '統計', icon: ChartBar },
  { value: 'goals' as const, label: '目標', icon: Flame },
  { value: 'journal' as const, label: '日記', icon: NotebookPen },
]

// ヘルスタブ（フルアクセスのみ）
const HEALTH_TAB = { value: 'health' as const, label: 'ヘルス', icon: ActivityIcon }

// クエリパラメータからタブ値を取得
const parseTabFromQuery = (tab: string | null, hasFullAccess: boolean): TabValue => {
  if (!tab) return 'timeline'
  if (tab === 'health' && !hasFullAccess) return 'timeline'
  if (TAB_VALUES.includes(tab as TabValue)) return tab as TabValue
  return 'timeline'
}

// クエリパラメータから日付を取得
const parseDateFromQuery = (dateStr: string | null): Date => {
  if (!dateStr) return new Date()
  try {
    const parsed = new Date(dateStr)
    // 有効な日付で未来でない場合のみ使用
    if (!isNaN(parsed.getTime()) && parsed <= new Date()) {
      return parsed
    }
  } catch {
    // パース失敗時は今日を返す
  }
  return new Date()
}

// クエリパラメータからビューモードを取得
const parseViewModeFromQuery = (mode: string | null): ViewMode =>
  mode === VIEW_MODE.CALENDAR || mode === 'calendar-scroll' ? VIEW_MODE.CALENDAR : VIEW_MODE.DAILY

// 旧 mode=infinite / calendar-scroll のリンクもスクロール表示として受け付ける
const parseScrollFromQuery = (params: URLSearchParams): boolean =>
  params.get(SCROLL_PARAM) === '1' || params.get('mode') === 'infinite' || params.get('mode') === 'calendar-scroll'

/** 読書履歴の取得件数 */
const READ_HISTORY_LIMIT = 500

export const ActivityPage = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { readHistory, fetchReadHistory, isLoadingHistory } = useProgressStore()
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)
  const { fetchDashboard } = useGarminStore()
  const upsertVisitInStore = useOverlandStore((s) => s.upsertVisit)
  const removeVisitFromStore = useOverlandStore((s) => s.removeVisit)
  // Visit の時間・場所を変えたら、レシートの紐づけを取り直す
  useVisitReceiptSync()
  const reviewedThroughDate = useActivityReviewStore((s) => s.reviewedThroughDate)
  const isReviewFetched = useActivityReviewStore((s) => s.isFetched)
  const fetchReviewProgress = useActivityReviewStore((s) => s.fetchProgress)

  // ヘッダのカレンダーでも使うので、表示モードに関係なくページで取得する
  useEffect(() => {
    if (!hasFullAccess || isReviewFetched) return
    void fetchReviewProgress()
  }, [hasFullAccess, isReviewFetched, fetchReviewProgress])

  const reviewedMarker = useMemo(
    () =>
      hasFullAccess && reviewedThroughDate
        ? {
            matcher: (d: Date) => format(d, 'yyyy-MM-dd') <= reviewedThroughDate,
            label: `確認済み（${formatDateCompact(reviewedThroughDate)} まで）`,
          }
        : undefined,
    [hasFullAccess, reviewedThroughDate],
  )

  // クエリパラメータからタブ、日付、ビューモードを取得
  const activeTab = parseTabFromQuery(searchParams.get('tab'), hasFullAccess)
  const dateParam = searchParams.get('date')
  const selectedDateStr = dateParam || format(new Date(), 'yyyy-MM-dd')
  // useMemoでDateオブジェクトを安定化（文字列が変わらない限り同じインスタンスを返す）
  const selectedDate = useMemo(() => parseDateFromQuery(dateParam), [dateParam])
  const viewMode = parseViewModeFromQuery(searchParams.get('mode'))
  const isScrollMode = parseScrollFromQuery(searchParams)

  // 別の画面・端末でのアクティビティ変更を受けて取り直す（ストアを使うのは日別タイムラインのみ）
  const isStoreTimeline = hasFullAccess && activeTab === 'timeline' && viewMode === VIEW_MODE.DAILY && !isScrollMode
  useActivityRemoteSync(isStoreTimeline ? selectedDateStr : null)

  // 表示モードとスクロール有無をまとめてクエリに書く（旧形式はここで正規化される）
  const updateView = (mode: ViewMode, scroll: boolean) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev)
      if (mode === VIEW_MODE.DAILY) {
        newParams.delete('mode') // デフォルトモードはクエリから削除
      } else {
        newParams.set('mode', mode)
      }
      if (scroll) {
        newParams.set(SCROLL_PARAM, '1')
      } else {
        newParams.delete(SCROLL_PARAM)
      }
      return newParams
    })
  }

  // タブ変更時にクエリを更新
  const setActiveTab = (tab: TabValue) => {
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev)
      if (tab === 'timeline') {
        newParams.delete('tab') // デフォルトタブはクエリから削除
      } else {
        newParams.set('tab', tab)
      }
      return newParams
    })
  }

  // 日付変更時にクエリを更新
  const setSelectedDate = (date: Date) => {
    const today = new Date()
    const isToday = format(date, 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev)
      if (isToday) {
        newParams.delete('date') // 今日の場合はクエリから削除
      } else {
        newParams.set('date', format(date, 'yyyy-MM-dd'))
      }
      return newParams
    })
  }

  // 確認済みの日へ移動（見直しの続きから再開する）
  const handleJumpToReviewedDate = (date: string) => setSelectedDate(new Date(`${date}T00:00:00`))

  // タブ構成（フルアクセスの場合はヘルスタブを追加）
  const TAB_CONFIG = hasFullAccess ? [...BASE_TABS, HEALTH_TAB] : BASE_TABS

  // タイムライン・ジャーナルタブでは選択日のみ取得、goals/statsタブでは全件取得
  const needsFullHistory = activeTab === 'goals' || activeTab === 'stats'
  const fullHistoryLoadedRef = useRef(false)

  useEffect(() => {
    if (needsFullHistory && !fullHistoryLoadedRef.current) {
      fullHistoryLoadedRef.current = true
      fetchReadHistory(READ_HISTORY_LIMIT)
    } else if (!needsFullHistory) {
      fetchReadHistory(READ_HISTORY_LIMIT, selectedDateStr)
    }
  }, [fetchReadHistory, needsFullHistory, selectedDateStr])

  // ヘルスタブ＋日付変更時にデータを取得（日付文字列で依存管理）
  useEffect(() => {
    if (activeTab === 'health' && hasFullAccess) {
      fetchDashboard(selectedDateStr)
    }
  }, [activeTab, hasFullAccess, selectedDateStr, fetchDashboard])

  // Place一覧シート

  // 選択モード（Visitの一括操作用）
  const [isSelectMode, setIsSelectMode] = useState(false)
  const [selectedVisitIds, setSelectedVisitIds] = useState<Set<number>>(new Set())
  const { isSubmitting: isMerging, run: runMerge } = useSubmitLock()
  const { isSubmitting: isDeleting, run: runDelete } = useSubmitLock()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)

  // タイムライン再読込用ref（TimelineViewから取得する関数を保持）
  const refreshTimelineRef = useRef<(() => void) | null>(null)
  const handleRefreshReady = useCallback((fn: (() => void) | null) => {
    refreshTimelineRef.current = fn
  }, [])

  // 選択モードを終了
  const handleCancelSelectMode = useCallback(() => {
    setIsSelectMode(false)
    setSelectedVisitIds(new Set())
  }, [])

  // Visit選択切り替え
  const handleToggleVisitSelect = useCallback((visitId: number) => {
    setSelectedVisitIds((prev) => {
      const next = new Set(prev)
      if (next.has(visitId)) {
        next.delete(visitId)
      } else {
        next.add(visitId)
      }
      return next
    })
  }, [])

  // マージ実行
  const handleMergeVisits = useCallback(async () => {
    if (selectedVisitIds.size < 2) return
    await runMerge(async () => {
      try {
        const result = await mergeVisits(Array.from(selectedVisitIds))
        toast.success(result.message)
        // 削除された visit を store から除外し、マージ後の最新 visit を upsert
        result.deleted_visit_ids.forEach((id) => removeVisitFromStore(id))
        try {
          const fresh = await getOverlandVisit(result.merged_visit_id)
          upsertVisitInStore(fresh)
        } catch {
          // 取得失敗時はフォールバックで refresh を呼ぶ
          refreshTimelineRef.current?.()
        }
        setSelectedVisitIds(new Set())
        setIsSelectMode(false)
      } catch {
        toast.error('マージに失敗しました')
      }
    })
  }, [selectedVisitIds, removeVisitFromStore, upsertVisitInStore, runMerge])

  // 削除実行
  const handleDeleteVisits = async () => {
    if (selectedVisitIds.size === 0) return
    await runDelete(async () => {
      const ids = Array.from(selectedVisitIds)
      const results = await Promise.allSettled(ids.map((id) => deleteVisit(id)))
      let succeeded = 0
      let failed = 0
      results.forEach((r, idx) => {
        if (r.status === 'fulfilled') {
          succeeded += 1
          const id = ids[idx]
          if (id !== undefined) removeVisitFromStore(id)
        } else {
          failed += 1
        }
      })
      if (failed > 0) {
        toast.error(`${succeeded}件削除、${failed}件失敗しました`)
      } else {
        toast.success(`${succeeded}件の訪問を削除しました`)
      }
      setSelectedVisitIds(new Set())
      setIsSelectMode(false)
      setIsDeleteDialogOpen(false)
    })
  }

  // ヘッダ下のツールバー: 表示モードのプルダウン（タイムラインタブのみ）
  const viewModeToolbar =
    activeTab === 'timeline' ? (
      <Select value={viewMode} onValueChange={(value) => updateView(value as ViewMode, isScrollMode)}>
        <SelectTrigger size="sm" className="w-32 border-none bg-transparent text-xs text-white/80 shadow-none">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {VIEW_MODE_ITEMS.map(({ value, label }) => (
            <SelectItem key={value} value={value}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    ) : undefined

  // ツールバー右側: 日付ごと／カレンダー共通のスクロール切り替え
  const toolbarItems: ToolbarItem[] = [
    {
      icon: <GalleryVerticalEnd size={18} className={isScrollMode ? 'text-blue-400' : 'text-white/80'} />,
      label: 'スクロール表示',
      onClick: () => updateView(viewMode, !isScrollMode),
      show: activeTab === 'timeline',
    },
  ]

  // ドロップダウンメニュー
  const dropdownSections: (RadioSection | ActionSection)[] = [
    {
      type: 'action',
      label: '場所管理',
      items: [
        {
          key: 'places-list',
          label: 'Place一覧',
          icon: <MapPin className="size-4" />,
          onClick: () => navigate(PATHS.PLACES),
        },
        {
          key: 'trips',
          label: '旅の記録',
          icon: <Luggage className="size-4" />,
          onClick: () => navigate(PATHS.TRIPS.LIST),
        },
        {
          key: 'rallies',
          label: 'スタンプラリー',
          icon: <Stamp className="size-4" />,
          onClick: () => navigate(PATHS.RALLIES.LIST),
        },
        {
          key: 'route-plans',
          label: 'ルートプラン',
          icon: <Route className="size-4" />,
          onClick: () => navigate(PATHS.ROUTE_PLANS.LIST),
        },
      ],
    },
    // タイムラインタブ＆日付ごとモード時は一括編集機能を追加
    ...(activeTab === 'timeline' && viewMode === VIEW_MODE.DAILY && !isScrollMode
      ? [
          {
            type: 'action' as const,
            label: '編集',
            items: [
              {
                key: 'batch-edit',
                label: '一括編集',
                icon: <CheckSquare className="size-4" />,
                onClick: () => setIsSelectMode(true),
              },
            ],
          },
        ]
      : []),
    // healthタブ時はリロード機能を追加
    ...(activeTab === 'health' && hasFullAccess
      ? [
          {
            type: 'action' as const,
            label: 'アクション',
            items: [{ key: 'reload', label: 'データを更新', onClick: () => fetchDashboard(selectedDateStr) }],
          },
        ]
      : []),
  ]

  return (
    <div className="flex flex-col h-screen">
      <Header
        title={<DateNavigator date={selectedDate} onDateChange={setSelectedDate} marker={reviewedMarker} />}
        onClose={() => navigate(PATHS.HOME)}
        dropdownSections={dropdownSections}
        customToolbar={viewModeToolbar}
        toolbarItems={toolbarItems}
      />

      <Tabs
        value={activeTab}
        onValueChange={(value) => setActiveTab(value as TabValue)}
        className="flex flex-col flex-1 overflow-hidden"
      >
        {/* コンテンツエリア */}
        <div className="flex-1 overflow-y-auto">
          <TabsContent value="timeline" className="mt-0 h-full">
            {viewMode === VIEW_MODE.CALENDAR ? (
              isScrollMode ? (
                <InfiniteCalendarView targetDate={selectedDateStr} />
              ) : (
                <CalendarDayView selectedDate={selectedDate} />
              )
            ) : isScrollMode ? (
              <InfiniteTimelineView targetDate={selectedDateStr} />
            ) : (
              <>
                <ReviewProgressBar targetDate={selectedDateStr} onJump={handleJumpToReviewedDate} />
                <TimelineView
                  history={readHistory}
                  isLoading={isLoadingHistory}
                  selectedDate={selectedDate}
                  isSelectMode={isSelectMode}
                  selectedVisitIds={selectedVisitIds}
                  onToggleVisitSelect={handleToggleVisitSelect}
                  onRefreshReady={handleRefreshReady}
                />
              </>
            )}
          </TabsContent>
          <TabsContent value="stats" className="mt-0 h-full">
            <StatsView history={readHistory} isLoading={isLoadingHistory} selectedDate={selectedDate} />
          </TabsContent>
          <TabsContent value="goals" className="mt-0 h-full">
            <GoalsView history={readHistory} isLoading={isLoadingHistory} selectedDate={selectedDate} />
          </TabsContent>
          <TabsContent value="journal" className="mt-0 h-full">
            <JournalView
              history={readHistory}
              isLoading={isLoadingHistory}
              selectedDate={selectedDate}
              selectedDateStr={selectedDateStr}
            />
          </TabsContent>
          {hasFullAccess && (
            <TabsContent value="health" className="mt-0 h-full">
              <HealthView selectedDateStr={selectedDateStr} />
            </TabsContent>
          )}
        </div>

        {/* 選択モード時のフッター */}
        {isSelectMode && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-background">
            <span className="text-sm text-muted-foreground">{selectedVisitIds.size}件選択中</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleCancelSelectMode} disabled={isMerging || isDeleting}>
                キャンセル
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => setIsDeleteDialogOpen(true)}
                disabled={selectedVisitIds.size === 0 || isMerging || isDeleting}
              >
                <Trash2 className="size-4 mr-1" />
                削除
              </Button>
              <Button
                size="sm"
                onClick={handleMergeVisits}
                disabled={selectedVisitIds.size < 2 || isMerging || isDeleting}
              >
                {isMerging ? (
                  <>
                    <Loader2 className="size-4 mr-1 animate-spin" />
                    マージ中...
                  </>
                ) : (
                  'マージ'
                )}
              </Button>
            </div>
          </div>
        )}

        {/* 下部固定タブ（選択モードでない時のみ） */}
        {!isSelectMode && (
          <TabsList className="w-full h-auto p-1 rounded-none border-t bg-background">
            {TAB_CONFIG.map(({ value, label, icon: Icon }) => (
              <TabsTrigger
                key={value}
                value={value}
                className="flex-1 flex-col gap-1 py-2 data-[state=active]:bg-muted"
              >
                <Icon className="size-5" />
                <span className="text-xs">{label}</span>
              </TabsTrigger>
            ))}
          </TabsList>
        )}
      </Tabs>

      {/* 削除確認ダイアログ */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>訪問を削除</AlertDialogTitle>
            <AlertDialogDescription>
              {selectedVisitIds.size}件の訪問を削除しますか？
              <br />
              この操作は取り消せません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteVisits}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="size-4 mr-2 animate-spin" />
                  削除中...
                </>
              ) : (
                '削除'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
